// Indian number formatting + utilities
export function formatINR(amount, opts = {}) {
  const decimals = opts.decimals ?? 2;
  if (amount === null || amount === undefined || amount === '') return '₹0.00';
  let num = Number(amount);
  if (Number.isNaN(num)) return '₹0.00';
  const isNeg = num < 0;
  num = Math.abs(num);
  // Round to the requested precision FIRST so e.g. 1234.7 with decimals=0
  // yields ₹1,235 (not ₹1,234 as plain Math.floor would have produced).
  // Multiplication by a power of 10 followed by Math.round is the standard
  // way to round-half-up at a given decimal place in JS.
  const scale = Math.pow(10, decimals);
  num = Math.round(num * scale) / scale;
  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * scale);
  let s = String(intPart);
  let formatted;
  if (s.length <= 3) {
    formatted = s;
  } else {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const parts = [];
    while (rest.length > 2) {
      parts.unshift(rest.slice(-2));
      rest = rest.slice(0, -2);
    }
    if (rest) parts.unshift(rest);
    formatted = parts.join(',') + ',' + last3;
  }
  const sign = isNeg ? '-' : '';
  const decStr = decimals > 0 ? '.' + String(decPart).padStart(decimals, '0') : '';
  return `₹${sign}${formatted}${decStr}`;
}

// Compact Indian INR for cramped UI surfaces (KPI subtitles, badges, etc.)
// Examples:
//   45            -> "₹45"
//   45000         -> "₹45,000"
//   350000        -> "₹3.50 L"     (3.5 lakh)
//   12500000      -> "₹1.25 Cr"    (1.25 crore)
//   negatives keep the sign; non-numeric → "₹0".
export function formatINRCompact(amount) {
  if (amount === null || amount === undefined || amount === '') return '₹0';
  const n = Number(amount);
  if (Number.isNaN(n)) return '₹0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e7) {
    // Crores
    const cr = abs / 1e7;
    return `${sign}₹${cr.toFixed(cr >= 100 ? 0 : 2)} Cr`;
  }
  if (abs >= 1e5) {
    // Lakhs
    const l = abs / 1e5;
    return `${sign}₹${l.toFixed(l >= 100 ? 0 : 2)} L`;
  }
  // For values below 1 lakh, use the full formatter w/o decimals so it stays compact.
  return formatINR(sign === '-' ? -abs : abs, { decimals: 0 });
}


export function formatCarats(v) {
  if (v === null || v === undefined) return '0.00';
  const n = Number(v);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
}

// -----------------------------------------------------------------------------
// Wave 3.68 — Formatted requirement label used across every "pick a
// requirement" dropdown (Order Detail → Issue Materials, Diamond /
// Colour-Stone / Pearl packet Issue dialogs, Goldsmith → Return dialog).
//
// Format:  ``<Shape> - <Size> - <Sieves> · pending <balC> ct / <balP> pcs``
//
// Missing bits are skipped gracefully.  The trailing ``pending`` suffix
// is derived from ``required_* - fulfilled_*`` so operators see what's
// still left to issue.  When neither carats nor pieces remain, we
// surface ``pending none`` instead of hiding the suffix — this makes
// zero-balance requirements obvious in the picker.
//
// Gold requirements have no shape/size/sieves; caller should fall back
// to the classification label + pending grams manually.
// -----------------------------------------------------------------------------
export function formatRequirementDropdownLabel(rq) {
  if (!rq) return 'Requirement';
  // Shape ← classification_label ``<code> — <shape>`` (Wave 3.16+).
  // Split on em-dash and take the last part; fall back to the whole
  // label when there's no dash (e.g. legacy free-text labels).
  const label = rq.classification_label || '';
  let shape = '';
  if (label.includes('—')) {
    shape = label.split('—').slice(1).join('—').trim();
  } else {
    shape = label.trim();
  }
  const size = (rq.size || '').toString().trim();
  const sieves = (rq.sieves || '').toString().trim();
  const parts = [];
  if (shape) parts.push(shape);
  if (size) parts.push(size);
  if (sieves) parts.push(sieves);
  // Description is a nice tie-breaker when nothing else is set (e.g.
  // legacy requirements that only had free-text remarks).
  const head = parts.length ? parts.join(' - ') : (rq.description || 'Requirement');
  // Balance = required - fulfilled, floored at zero.
  const balC = Math.max(0, Number(rq.required_carats || 0) - Number(rq.fulfilled_carats || 0));
  const balP = Math.max(0, Number(rq.required_pieces || 0) - Number(rq.fulfilled_pieces || 0));
  const bits = [];
  if (balC > 0) bits.push(`${formatCarats(balC)} ct`);
  if (balP > 0) bits.push(`${balP} pcs`);
  const suffix = bits.length ? ` · pending ${bits.join(' / ')}` : ' · pending none';
  return `${head}${suffix}`;
}

export function formatGrams(v) {
  if (v === null || v === undefined) return '0.000';
  const n = Number(v);
  if (Number.isNaN(n)) return '0.000';
  return n.toFixed(3);
}

export function formatNumber(v) {
  if (v === null || v === undefined) return '0';
  const n = Number(v);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('en-IN');
}

export function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }) + ' IST';
  } catch { return iso; }
}

export function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST';
  } catch { return iso; }
}

export function toISODateInput(d = new Date()) {
  // For HTML date input value yyyy-mm-dd
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// IST-aware helpers for <input type="datetime-local"> fields.
//
// The whole app reads/writes timestamps in Asia/Kolkata (IST, fixed UTC+5:30,
// no DST). A native datetime-local control, however, is timezone-agnostic and
// used to be seeded with a UTC string (``new Date().toISOString()``) which made
// it display 5:30h behind the real IST wall-clock. These two helpers keep the
// control on IST regardless of the visitor's device timezone:
//   • nowISTDatetimeLocal()      → current IST wall-clock as "YYYY-MM-DDTHH:mm"
//   • istDatetimeLocalToISO(val) → interpret a "YYYY-MM-DDTHH:mm" as IST and
//                                  return the equivalent UTC ISO string.
const _IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000; // +05:30

export function nowISTDatetimeLocal(date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(date);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    let hour = get('hour');
    if (hour === '24') hour = '00'; // some engines emit 24 for midnight
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
  } catch {
    // Fallback: shift the UTC instant by the fixed IST offset.
    const shifted = new Date(date.getTime() + _IST_OFFSET_MS);
    return shifted.toISOString().slice(0, 16);
  }
}

export function istDatetimeLocalToISO(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const h = Number(m[4]); const mi = Number(m[5]);
  // Treat the wall-clock as IST, then subtract the offset to get true UTC.
  const utcMs = Date.UTC(y, mo - 1, d, h, mi) - _IST_OFFSET_MS;
  return new Date(utcMs).toISOString();
}

// Wave 1.1 (revised) — Calculate IST calendar-day difference between an ISO
// timestamp and "now". A new day starts at IST midnight (00:00 +05:30), so
// the count ticks over at India's date change regardless of the user's
// browser timezone.
//
// Returns:
//   • null  → invalid input
//   • 0     → same IST calendar day
//   • N > 0 → N IST calendar days have passed since the timestamp
//   • N < 0 → timestamp is in the future (rare; clamp at 0 in UI)
export function daysSinceIST(iso, now = new Date()) {
  if (!iso) return null;
  try {
    const created = new Date(iso);
    if (Number.isNaN(created.getTime())) return null;
    // Shift both timestamps to IST by adding 5h30m, then take the UTC date
    // components — this gives us the IST calendar date (Y-M-D) regardless of
    // the browser's local timezone.
    const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
    const istCreated = new Date(created.getTime() + IST_OFFSET_MS);
    const istNow = new Date(now.getTime() + IST_OFFSET_MS);
    // Build midnight-of-day for each in UTC to drop time-of-day
    const day = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const diff = Math.round((day(istNow) - day(istCreated)) / 86400000);
    return diff;
  } catch {
    return null;
  }
}

// Friendly formatter used across Orders pages — "Today" / "1 day" / "X days".
export function formatDaysSince(iso, now = new Date()) {
  const d = daysSinceIST(iso, now);
  if (d === null) return '—';
  if (d <= 0) return 'Today';
  return `${d} day${d === 1 ? '' : 's'}`;
}
